import { computed, ref } from 'vue';
import { freedomApp } from 'freedom-shared';
import { useRouter } from 'vue-router';

import { sentryModel } from '@/shared/model';
import { amplitudeEventsLib } from '@/shared/lib';

import { useBase64Deeplink } from './lib';

type ShareLinkParams = {
  url: string;
  title?: string;
  source?: string;
};

export const useShare = () => {
  const router = useRouter();
  const base64Deeplink = useBase64Deeplink();

  const deeplinkUrl = ref('');
  const isDeeplinkGenerating = ref(false);

  const isShareAvailable = computed(() => {
    const isAndroidShareAvailable = !!window.FreedomApp?.share;
    const isWebShareAvailable = !!navigator.share && !!navigator.canShare;
    return isAndroidShareAvailable || isWebShareAvailable;
  });

  const generateDeeplink = async (pathOverride?: string) => {
    if (deeplinkUrl.value) {
      return deeplinkUrl.value;
    }

    isDeeplinkGenerating.value = true;

    let { path } = router.currentRoute.value;

    if (pathOverride) {
      path = pathOverride;
    }

    try {
      const url = await base64Deeplink.createBase64Deeplink(path);
      deeplinkUrl.value = url ?? '';

      return deeplinkUrl.value;
    } catch (error) {
      sentryModel.captureException(
        sentryModel.getReadableError('ERR_DEEPLINK_FAILED', error as Error),
      );
      throw error;
    } finally {
      isDeeplinkGenerating.value = false;
    }
  };

  const shareLink = async ({ url, title, source }: ShareLinkParams) => {
    try {
      amplitudeEventsLib.shareButtonClicked({ source });
      await freedomApp.lib.share({
        title,
        text: 'Freedom Travel',
        url,
      });
    } catch (error) {
      sentryModel.captureException(
        sentryModel.getReadableError('ERR_SHARE_FAILED', error as Error),
      );
    }
  };

  const requestShare = async ({
    title,
    url: pathOverride,
    source,
  }: Partial<ShareLinkParams>) => {
    if (deeplinkUrl.value) {
      return shareLink({ url: deeplinkUrl.value, title, source });
    }

    const url = await generateDeeplink(pathOverride);

    if (url) {
      return shareLink({ url, title, source });
    }
  };

  return {
    deeplinkUrl,
    isDeeplinkGenerating,
    isShareAvailable,
    generateDeeplink,
    shareLink,
    requestShare,
  };
};
